import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { plateAccessTypeOptions } from "../../../utils/Common";
import { formatUTCTimeWithSec } from "../../../global/utils/unixToLocalTime";

function formatCallDuration(seconds) {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total < 0) return "";
  const mins = Math.floor(total / 60);
  const secs = Math.floor(total % 60);
  if (mins <= 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

function accessTypeLabel(value) {
  if (value == null || value === "") return "";
  const match = plateAccessTypeOptions.find(
    (opt) => Number(opt.value) === Number(value),
  );
  return match?.label ?? String(value);
}

function accessLabel(record) {
  if (Number(record?.AccessGranted) === 1) return "Granted";
  if (Number(record?.AccessGranted) === 0) return "Denied";
  return record?.AccessGrantedLabel || "";
}

function unlockedFromLabel(record) {
  return (
    record?.ControlFromLabel ||
    (Number(record?.ControlFrom) === 1 ? "Directory" : "Virtual Intercom")
  );
}

const CALL_HISTORY_PDF_HEADERS = [
  "No",
  "Time",
  "Device Name",
  "Answered By",
  "Access Type",
  "Access Item",
  "Access",
  "Unlocked From",
  "Call Duration",
];

export const buildCallHistoryPdfRows = (rows = []) =>
  rows.map((row) => [
    row?.CallLogNo ?? "",
    row?.LogTime ? formatUTCTimeWithSec(row.LogTime) : "",
    row?.DeviceName ?? "",
    row?.PersonName ?? "",
    accessTypeLabel(row?.AccessType),
    row?.AccessItemName ?? "",
    accessLabel(row),
    unlockedFromLabel(row),
    formatCallDuration(row?.CallDuration),
  ]);

export const buildCallHistoryFilterSummary = (params = {}) => {
  const parts = [];
  if (params.PersonName) parts.push(`Answered By: ${params.PersonName}`);
  if (params.AccessItemName) parts.push(`Access Item: ${params.AccessItemName}`);
  if (params.AccessType !== undefined && params.AccessType !== "") {
    parts.push(`Access Type: ${accessTypeLabel(params.AccessType)}`);
  }
  if (params.ControlFrom !== undefined && params.ControlFrom !== "") {
    parts.push(
      `Unlocked From: ${
        Number(params.ControlFrom) === 1 ? "Directory" : "Virtual Intercom"
      }`,
    );
  }
  if (params.AccessGranted !== undefined && params.AccessGranted !== "") {
    parts.push(
      `Access: ${Number(params.AccessGranted) === 1 ? "Granted" : "Denied"}`,
    );
  }
  if (params.DeviceName) parts.push(`Device Name: ${params.DeviceName}`);
  if (params.DeviceNo) parts.push(`Device No: ${params.DeviceNo}`);
  if (params.LogTime) parts.push(`Month: ${params.LogTime}`);
  return parts.length ? parts.join("  |  ") : "";
};

export const generateCallHistoryPDF = (rows = [], filterParams = {}) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  doc.setFontSize(16);
  doc.setFont(undefined, "bold");
  doc.text("Call History Report", 14, 15);

  doc.setFontSize(10);
  doc.setFont(undefined, "normal");
  const d = new Date();
  const generatedAt =
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0") +
    " " +
    String(d.getHours()).padStart(2, "0") +
    ":" +
    String(d.getMinutes()).padStart(2, "0");
  doc.text(`Generated: ${generatedAt}`, 14, 22);
  doc.text(`Total: ${rows.length}`, 14, 28);

  const filterSummary = buildCallHistoryFilterSummary(filterParams);
  let tableStartY = 34;
  if (filterSummary) {
    const filterLines = doc.splitTextToSize(`Filter: ${filterSummary}`, 270);
    doc.text(filterLines, 14, 34);
    tableStartY = 34 + filterLines.length * 5 + 2;
  }

  autoTable(doc, {
    startY: tableStartY,
    head: [CALL_HISTORY_PDF_HEADERS],
    body: buildCallHistoryPdfRows(rows),
    styles: {
      fontSize: 7,
      cellPadding: 1.5,
      lineColor: [200, 200, 200],
      lineWidth: 0.1,
      valign: "middle",
    },
    headStyles: {
      fillColor: [68, 114, 196],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 7,
      halign: "center",
    },
    margin: { top: 20, left: 8, right: 8 },
  });

  return doc;
};

export const downloadCallHistoryPDF = (
  rows,
  filterParams = {},
  filename = "call_history_report.pdf",
) => {
  const doc = generateCallHistoryPDF(rows, filterParams);
  doc.save(filename);
};

export const printCallHistoryPDF = (rows, filterParams = {}) => {
  const doc = generateCallHistoryPDF(rows, filterParams);
  doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank");
};
